// --- START FILE: public/js/managers/AchievementManager.js ---

/**
 * AchievementManager.js v1.32.1 - FIX: Missing getTierEmoji helper function
 * Gestisce la logica degli achievement e la visualizzazione delle notifiche toast.
 * FIX: Aggiunta la funzione helper getTierEmoji all'interno dello scope del modulo
 *      per risolvere il ReferenceError causato dal bundling con Parcel.
 * MANTIENE: Logica completa della v1.32.
 */

// --- FUNZIONE HELPER AGGIUNTA QUI ---
/**
 * Restituisce un'emoji basata sul tier dell'achievement.
 * @param {string} tier - Il tier ('bronze', 'silver', 'gold', 'platinum').
 * @returns {string} L'emoji corrispondente.
 */
function getTierEmoji(tier) {
    switch (tier) {
        case 'platinum': return '🏆';
        case 'gold': return '🥇';
        case 'silver': return '🥈';
        case 'bronze': return '🥉';
        default: return '❔';
    }
}
// --- FINE FUNZIONE HELPER ---


class AchievementManager {
    constructor(profileManager, game) {
        // console.log("[AchMgr] Constructor v1.32.1 (FIX: getTierEmoji)");

        if (!profileManager || typeof profileManager.unlockAchievementLocally !== 'function') {
            console.error("[AchMgr Constructor Error v1.32.1] Istanza di ProfileManager non valida!");
            this.initializationError = true;
            return;
        }
        if (!game || typeof game !== 'object') {
            console.error("[AchMgr Constructor Error v1.32.1] Oggetto Game non valido!");
            this.initializationError = true;
            return;
        }
        this.profileManager = profileManager;
        this.game = game;
        this.isLandingContext = !(game.scene?.game?.isBooted === true);
        this.initializationError = false;

        if (typeof window.ACHIEVEMENT_DEFINITIONS !== 'undefined') {
            this.definitions = window.ACHIEVEMENT_DEFINITIONS;
            this.definitionsById = this.definitions.reduce((map, ach) => {
                map[ach.id] = ach;
                return map;
            }, {});
        } else {
            console.error("[AchMgr Constructor Error v1.32.1] Definizioni ACHIEVEMENT_DEFINITIONS non trovate!");
            this.definitions = [];
            this.definitionsById = {};
            this.initializationError = true;
            return;
        }

        this.isInitialized = false;
        this.unlockedInSession = new Set();
        this.gameEventEmitter = null;
        this.boundHandleAuthEvent = this.handleAuthEvent.bind(this);
        this.achievementToastQueue = [];
        this.pendingLandingNotifications = [];
        this.isShowingAchievementToast = false;
        this.achievementToastTimer = null;
        this.landingToastHideTimer = null;
        this.uiSceneRef = null;
        this.isTransitioning = false;
    }

    initialize(eventEmitter = null) {
        if (this.initializationError || this.isInitialized) {
            return;
        }
        document.removeEventListener('firebase-auth', this.boundHandleAuthEvent);
        document.addEventListener('firebase-auth', this.boundHandleAuthEvent);

        if (!this.isLandingContext) {
            if (!eventEmitter || typeof eventEmitter.on !== 'function' || typeof eventEmitter.off !== 'function') {
                console.error("[AchMgr Initialize v1.32.1] eventEmitter non valido fornito nel contesto Phaser!");
            } else {
                this.gameEventEmitter = eventEmitter;
                const gameEventsToListen = [
                    'scoreUpdated', 'gameWon', 'gameOver', 'pelletCollected', 'powerPelletCollected',
                    'ghostEatenInPowerMode', 'massaraCollected', 'furlaniFuryActivated',
                    'cardinaleFuryActivated', 'florenziTouched', 'kessieExpired', 'kessieRenewed',
                    'origiSlowdownActivated', 'florenziRiscattoCompleted', 'levelCompleted',
                    'florenziFastRiscattoCompleted', 'cardinaleFurySurvivedWithoutPDF',
                    'origiSlowdownSurvived'
                ];
                gameEventsToListen.forEach(eventName => {
                    this.gameEventEmitter.off(eventName, this.handleGameEvent, this); // Pulisci listener precedenti
                    this.gameEventEmitter.on(eventName, data => this.handleGameEvent(eventName, data), this);
                });

                this.gameEventEmitter.off('progressTrackerUpdated', this.handleProgressTrackerUpdate, this);
                this.gameEventEmitter.on('progressTrackerUpdated', this.handleProgressTrackerUpdate, this);

                this.gameEventEmitter.off('levelStatUpdated', this.handleLevelStatUpdate, this);
                this.gameEventEmitter.on('levelStatUpdated', this.handleLevelStatUpdate, this);

                this.gameEventEmitter.off('transitionStarted', this.handleTransitionStart, this);
                this.gameEventEmitter.on('transitionStarted', this.handleTransitionStart, this);
                this.gameEventEmitter.off('transitionEnded', this.handleTransitionEnd, this);
                this.gameEventEmitter.on('transitionEnded', this.handleTransitionEnd, this);
            }
        } else { // Landing Context
            setInterval(() => this._processAchievementQueue(), 500);
        }
        this.isInitialized = true;
        this.unlockedInSession.clear();
        // console.log("[AchMgr Initialize v1.32.1] Inizializzazione completata.");
    }

    update(time, delta) {
        /* Non utilizzata al momento */
    }

    getDefinitions() {
        return this.definitions;
    }

    getAchievement(id) {
        return this.definitionsById[id] || null;
    }

    handleAuthEvent(event) {
        if (this.initializationError || !this.isInitialized) return;
        const {
            type
        } = event.detail;
        if (type === 'userRegistered') {
            this.attemptUnlock('register_account');
        }
    }

    handleGameEvent(eventName, data = {}) {
        if (this.initializationError || !this.isInitialized || this.isLandingContext) return;
        if (!this.profileManager || !this.profileManager.getUserData()) {
            // console.warn("[AchMgr handleGameEvent v1.32.1] ProfileManager o dati utente non disponibili.");
            return;
        }
        const unlockedAchievements = this.profileManager.getUserAchievements() || {};

        for (const achDef of this.definitions) {
            if (unlockedAchievements[achDef.id] || !achDef.criteria || !achDef.criteria.event) continue;
            if (achDef.criteria.event !== eventName) continue;

            if (achDef.criteria.type === 'counter_reach') {
                continue; // Gestito da handleProgressTrackerUpdate e handleLevelStatUpdate
            }

            if (achDef.criteria.type === 'state_at_level_end' && eventName !== 'levelCompleted') continue;
            if (achDef.criteria.type === 'multi_state_at_game_over' && eventName !== 'gameOver') continue;

            if (this._checkCriteriaMatch(achDef, eventName, data)) {
                console.log(`%c[AchMgr handleGameEvent v1.32.1] Criterio SODDISFATTO per ${achDef.id} da evento ${eventName}`, 'color: yellow;');
                this.attemptUnlock(achDef.id);
            }
        }
    }

    handleProgressTrackerUpdate(data) {
        if (this.initializationError || !this.isInitialized || this.isLandingContext) return;
        if (!data || !data.key || data.value === undefined) {
            // console.warn("[AchMgr handleProgressTrackerUpdate v1.32.1] Dati evento non validi:", data);
            return;
        }
        const trackerKey = data.key;
        const trackerValue = data.value;
        const unlockedAchievements = this.profileManager?.getUserAchievements() || {};

        for (const achDef of this.definitions) {
            if (unlockedAchievements[achDef.id] || !achDef.criteria || achDef.criteria.type !== 'counter_reach') continue;
            // Ignora per-level, gestito da handleLevelStatUpdate
            if (achDef.criteria.scope === 'per-level') continue;
            if (achDef.criteria.counterKey !== trackerKey) continue;

            if (typeof trackerValue === 'number' && trackerValue >= achDef.criteria.count) {
                console.log(`%c[AchMgr handleProgressTrackerUpdate v1.32.1] Criterio counter_reach (lifetime/per-game) SODDISFATTO per ${achDef.id} (tracker ${trackerKey}: ${trackerValue} >= ${achDef.criteria.count})`, 'color: yellow;');
                this.attemptUnlock(achDef.id);
            }
        }
    }

    handleLevelStatUpdate(data) {
        if (this.initializationError || !this.isInitialized || this.isLandingContext) return;
        if (!data || !data.key || data.value === undefined) {
            // console.warn("[AchMgr handleLevelStatUpdate v1.32.1] Dati evento non validi:", data);
            return;
        }
        const statKey = data.key;
        const statValue = data.value;
        const unlockedAchievements = this.profileManager?.getUserAchievements() || {};

        for (const achDef of this.definitions) {
            if (unlockedAchievements[achDef.id] ||
                !achDef.criteria ||
                achDef.criteria.type !== 'counter_reach' ||
                achDef.criteria.scope !== 'per-level') {
                continue;
            }
            if (achDef.criteria.counterKey !== statKey) continue;

            if (typeof statValue === 'number' && statValue >= achDef.criteria.count) {
                console.log(`%c[AchMgr handleLevelStatUpdate v1.32.1] Criterio counter_reach (per-level) SODDISFATTO per ${achDef.id} (stat ${statKey}: ${statValue} >= ${achDef.criteria.count})`, 'color: yellow;');
                this.attemptUnlock(achDef.id);
            }
        }
    }

    handleTransitionStart() {
        if (this.isLandingContext) return;
        this.isTransitioning = true;
        // console.log("[AchMgr handleTransitionStart v1.32.1] Transizione iniziata.");
    }

    handleTransitionEnd() {
        if (this.isLandingContext) return;
        this.isTransitioning = false;
        // console.log("[AchMgr handleTransitionEnd v1.32.1] Transizione terminata, processo coda.");
        this._processAchievementQueue();
    }

    _checkCriteriaMatch(achDef, eventName, eventData = {}) {
        const criteria = achDef.criteria;
        if (!criteria) return false;

        switch (criteria.type) {
            case 'event_flag':
                return true;
            case 'score_reach':
                return eventData?.score !== undefined && typeof eventData.score === 'number' && eventData.score >= criteria.value;
            case 'score_at_victory':
                return eventData?.finalScore !== undefined && typeof eventData.finalScore === 'number' && eventData.finalScore >= criteria.minValue;
            case 'event_count':
                return criteria.count === 1;
            case 'event_data_match':
                if (criteria.dataKey === 'levelNumber') {
                    return eventData?.levelNumber === criteria.expectedValue;
                }
                if (criteria.dataKey === 'comboCount') {
                    return eventData?.comboCount === criteria.expectedValue;
                }
                return false;
            case 'state_at_level_end':
                if (!eventData || !eventData.levelStates || eventData.levelNumber === undefined) {
                    return false;
                }
                const levelNumber = eventData.levelNumber;
                const levelStates = eventData.levelStates;
                let levelConditionMet = true;
                if (criteria.minLevel && levelNumber < criteria.minLevel) levelConditionMet = false;
                if (criteria.levelNumber && levelNumber !== criteria.levelNumber) levelConditionMet = false;
                if (!levelConditionMet) return false;

                if (criteria.requiredStates) {
                    for (const stateKey in criteria.requiredStates) {
                        if (levelStates[stateKey] !== criteria.requiredStates[stateKey]) {
                            return false;
                        }
                    }
                    return true;
                } else if (criteria.state && criteria.expectedValue !== undefined) {
                    return levelStates[criteria.state] === criteria.expectedValue;
                }
                return false;
            case 'multi_state_at_game_over':
                if (eventName !== 'gameOver' || !eventData || eventData.currentLevel === undefined || !eventData.levelStates) {
                    return false;
                }
                const currentLevelAtGameOver = eventData.currentLevel;
                if (criteria.requiredLevel && currentLevelAtGameOver !== criteria.requiredLevel) {
                    return false;
                }
                const levelStatesAtGameOver = eventData.levelStates;
                let allStatesMatch = true;
                for (const stateKey in criteria.requiredStates) {
                    const expected = criteria.requiredStates[stateKey];
                    const actual = levelStatesAtGameOver[stateKey];
                    if (actual !== expected) {
                        allStatesMatch = false;
                        break;
                    }
                }
                return allStatesMatch;
            case 'counter_reach':
            case 'all_others_unlocked':
            case 'manual_unlock_group':
                return false;
            default:
                console.warn(`[AchMgr _checkCriteriaMatch v1.32.1] Tipo criterio sconosciuto: ${criteria.type} per ${achDef.id}`);
                return false;
        }
    }

    _isExternalAchievement(achievementId) {
        const externalIds = ['visit_twitch', 'visit_youtube', 'visit_x', 'visit_tiktok'];
        return externalIds.includes(achievementId);
    }

    attemptUnlock(achievementId) {
        if (this.initializationError || !this.profileManager) {
            return;
        }
        if (this.unlockedInSession.has(achievementId)) {
            return;
        }

        const authMgr = window.authManager;
        if (!authMgr || !authMgr.isAuthenticated()) {
            return;
        }

        const isNewlyUnlockedLocally = this.profileManager.unlockAchievementLocally(achievementId);

        if (isNewlyUnlockedLocally) {
            console.log(`%c[AchMgr attemptUnlock v1.32.1] NUOVO SBLOCCO LOCALE: ${achievementId}!`, 'color: #00ff00; font-weight: bold;');
            this.unlockedInSession.add(achievementId);
            const achievementData = this.getAchievement(achievementId);

            if (achievementData) {
                const isExternal = this._isExternalAchievement(achievementId);
                if (isExternal && this.isLandingContext) {
                    if (!this.pendingLandingNotifications.some(p => p.id === achievementId)) {
                        this.pendingLandingNotifications.push(achievementData);
                    }
                } else {
                    this.achievementToastQueue.push(achievementData);
                    this._processAchievementQueue();
                }

                this.profileManager.writeUserProfileData();

                if (achievementId !== 'platinum_trophy') {
                    this.checkPlatinumStatus();
                }
            } else {
                console.warn(`[AchMgr attemptUnlock v1.32.1] Dati achievement non trovati per ID ${achievementId}.`);
            }
        }
    }

    attemptUnlockInternal(achievementId) {
        if (this.initializationError || !this.profileManager) return;
        if (this.unlockedInSession.has(achievementId)) return;

        const authMgr = window.authManager;
        if (!authMgr || !authMgr.isAuthenticated()) return;

        const isNewlyUnlockedLocally = this.profileManager.unlockAchievementLocally(achievementId);
        if (isNewlyUnlockedLocally) {
            console.log(`%c[AchMgr attemptUnlockInternal v1.32.1] NUOVO SBLOCCO LOCALE (PLATINO?): ${achievementId}!`, 'color: #00ff00; font-weight: bold;');
            this.unlockedInSession.add(achievementId);
            const achievementData = this.getAchievement(achievementId);
            if (achievementData) {
                if (this.isLandingContext) {
                    if (!this.pendingLandingNotifications.some(p => p.id === achievementId)) {
                        this.pendingLandingNotifications.push(achievementData);
                    }
                } else {
                    this.achievementToastQueue.push(achievementData);
                    this._processAchievementQueue();
                }
                this.profileManager.writeUserProfileData();
            } else {
                console.warn(`[AchMgr attemptUnlockInternal v1.32.1] Dati achievement non trovati per ID ${achievementId}.`);
            }
        }
    }

    checkPlatinumStatus() {
        if (this.initializationError || !this.profileManager || !this.definitions || this.definitions.length === 0) {
            return;
        }
        const unlockedAchievements = this.profileManager.getUserAchievements() || {};
        const totalDefinedAchievements = this.definitions.length;
        const requiredCountForPlatinum = totalDefinedAchievements - 1;

        if (unlockedAchievements['platinum_trophy'] || requiredCountForPlatinum <= 0) {
            return;
        }

        let currentUnlockedCountExcludingPlatinum = 0;
        for (const achId in unlockedAchievements) {
            if (achId !== 'platinum_trophy') {
                currentUnlockedCountExcludingPlatinum++;
            }
        }

        if (currentUnlockedCountExcludingPlatinum >= requiredCountForPlatinum) {
            const allOtherIds = this.definitions.map(a => a.id).filter(id => id !== 'platinum_trophy');
            let allFound = true;
            for (const reqId of allOtherIds) {
                if (!unlockedAchievements[reqId]) {
                    allFound = false;
                    console.warn(`[AchMgr checkPlatinumStatus v1.32.1] Discrepanza! Manca ${reqId}.`);
                    break;
                }
            }
            if (allFound) {
                console.log(`%c[AchMgr checkPlatinumStatus v1.32.1] Tento sblocco Platino!`, 'color: magenta; font-weight: bold;');
                this.attemptUnlockInternal('platinum_trophy');
            }
        }
    }

    processPendingLandingNotifications() {
        if (!this.isLandingContext || this.pendingLandingNotifications.length === 0) {
            return;
        }
        while (this.pendingLandingNotifications.length > 0) {
            const achievementData = this.pendingLandingNotifications.shift();
            this.achievementToastQueue.push(achievementData);
        }
        this._processAchievementQueue();
    }

    _processAchievementQueue() {
        if (this.isShowingAchievementToast || this.achievementToastQueue.length === 0) {
            return;
        }
        if (!this.isLandingContext && this.isTransitioning) {
            return;
        }

        const achievementData = this.achievementToastQueue.shift();
        if (!achievementData) return;

        if (this.isLandingContext) {
            this._showLandingToast(achievementData);
        } else {
            const uiScene = this._getUIScene();
            if (uiScene) {
                this._showAchievementToast(uiScene, achievementData);
            } else {
                this.achievementToastQueue.unshift(achievementData);
                this.isShowingAchievementToast = false;
            }
        }
    }

    _getUIScene() {
        if (this.uiSceneRef && this.uiSceneRef.scene?.key === 'UIScene' && this.uiSceneRef.scene.settings.active) {
            return this.uiSceneRef;
        }
        try {
            const uiScene = this.game.scene.getScene('UIScene');
            if (uiScene && uiScene.scene.settings.active) {
                this.uiSceneRef = uiScene;
                return uiScene;
            }
        } catch (e) {}
        this.uiSceneRef = null;
        return null;
    }

    _showAchievementToast(uiScene, achievementData) {
        if (!uiScene || !uiScene.scene?.settings?.active || this.isShowingAchievementToast) return;
        this.isShowingAchievementToast = true;

        let soundToPlay = null;
        if (achievementData.tier === 'platinum') soundToPlay = 'platinum';
        else if (['bronze', 'silver', 'gold'].includes(achievementData.tier)) soundToPlay = 'bronze_silver_gold';

        if (soundToPlay && window.audioManager?.play_sound) {
            window.audioManager.play_sound(soundToPlay);
        }

        const padding = 8;
        const iconSize = 50;
        const spacing = 4;
        const borderRadius = 6;
        const titleFontSize = Math.max(11, Math.floor(uiScene.cellSize * 0.22));
        const nameFontSize = Math.max(10, Math.floor(uiScene.cellSize * 0.20));
        const tierColors = {
            bronze: 0xCD7F32,
            silver: 0xC0C0C0,
            gold: 0xFFD700,
            platinum: 0xE5E4E2,
            default: 0x555555
        };
        const borderColor = tierColors[achievementData.tier] || tierColors.default;
        const bgColor = 0x111111;

        if (!this.achievementToastContainer || !this.achievementToastContainer.scene || this.achievementToastContainer.parentContainer === null) {
            if (this.achievementToastContainer) try {
                this.achievementToastContainer.destroy();
            } catch (e) {}
            this.achievementToastContainer = uiScene.add.container(0, 0).setDepth(5000).setAlpha(0);
        } else {
            this.achievementToastContainer.removeAll(true);
            this.achievementToastContainer.setAlpha(0).setPosition(0, 0).setVisible(true);
        }
        uiScene.add.existing(this.achievementToastContainer);

        const background = uiScene.add.graphics();
        this.achievementToastContainer.add(background);
        const toastTitle = uiScene.add.text(0, 0, "🏆 Trofeo Sbloccato!", {
            fontFamily: 'Arcade, Arial',
            fontSize: `${titleFontSize}px`,
            color: '#FFFF00',
            align: 'left',
            stroke: '#000000',
            strokeThickness: 2
        }).setOrigin(0, 0);
        this.achievementToastContainer.add(toastTitle);
        const maxWidth = uiScene.cameras.main.width * 0.45;
        const achievementName = uiScene.add.text(0, 0, achievementData.name, {
            fontFamily: 'Arial, sans-serif',
            fontSize: `${nameFontSize}px`,
            color: '#FFFFFF',
            align: 'left',
            fontStyle: 'italic',
            wordWrap: {
                width: maxWidth - padding * 2 - iconSize - spacing
            }
        }).setOrigin(0, 0);
        this.achievementToastContainer.add(achievementName);

        let achievementIconDisplay = null;
        const parcelIconUrl = achievementData.icon;

        const createOrUpdateIconDisplay = (textureKeyForPhaser) => {
            if (!this.achievementToastContainer?.active) return;
            const existingIcon = this.achievementToastContainer.getByName('toastIconDisplay');
            if (existingIcon) existingIcon.destroy();
            achievementIconDisplay = uiScene.add.image(0, 0, textureKeyForPhaser).setName('toastIconDisplay').setDisplaySize(iconSize, iconSize).setOrigin(0, 0.5);
            this.achievementToastContainer.add(achievementIconDisplay);
            this._layoutToastContent(uiScene, background, toastTitle, achievementName, achievementIconDisplay, padding, spacing, iconSize, borderRadius, bgColor, borderColor);
        };

        if (parcelIconUrl) {
            const phaserTextureKey = `ach_toast_${achievementData.id}`;
            if (uiScene.textures.exists(phaserTextureKey)) {
                createOrUpdateIconDisplay(phaserTextureKey);
            } else {
                const emojiFallback = uiScene.add.text(0, 0, getTierEmoji(achievementData.tier), {
                    fontSize: `${Math.floor(iconSize * 0.8)}px`
                }).setName('toastIconDisplay').setOrigin(0, 0.5);
                this.achievementToastContainer.add(emojiFallback);
                achievementIconDisplay = emojiFallback;
                this._layoutToastContent(uiScene, background, toastTitle, achievementName, achievementIconDisplay, padding, spacing, iconSize, borderRadius, bgColor, borderColor);

                uiScene.load.once(`filecomplete-image-${phaserTextureKey}`, () => {
                    if (this.achievementToastContainer?.active && achievementData.id === this.achievementToastContainer.getData('currentAchId')) {
                        createOrUpdateIconDisplay(phaserTextureKey);
                    }
                });
                uiScene.load.image(phaserTextureKey, parcelIconUrl);
                if (!uiScene.load.isLoading()) {
                    try {
                        uiScene.load.start();
                    } catch (e) {}
                }
            }
        } else {
            achievementIconDisplay = uiScene.add.text(0, 0, getTierEmoji(achievementData.tier), {
                fontSize: `${Math.floor(iconSize * 0.8)}px`
            }).setName('toastIconDisplay').setOrigin(0, 0.5);
            this.achievementToastContainer.add(achievementIconDisplay);
            this._layoutToastContent(uiScene, background, toastTitle, achievementName, achievementIconDisplay, padding, spacing, iconSize, borderRadius, bgColor, borderColor);
        }
        this.achievementToastContainer.setData('currentAchId', achievementData.id);

        const screenPadding = 15;
        const startX = uiScene.cameras.main.width;
        const bounds = this.achievementToastContainer.getBounds();
        const widthMultiplier = 1.20;
        const estimatedWidth = bounds.width > 0 ? bounds.width * widthMultiplier : 300;
        const endX = uiScene.cameras.main.width - estimatedWidth - screenPadding;

        this.achievementToastContainer.setPosition(startX, screenPadding).setAlpha(1);
        uiScene.tweens.add({
            targets: this.achievementToastContainer,
            x: endX,
            duration: 400,
            ease: 'Cubic.easeOut'
        });

        const displayDuration = 3000;
        if (this.achievementToastTimer) {
            if (typeof this.achievementToastTimer.remove === 'function') {
                this.achievementToastTimer.remove();
            } else {
                clearTimeout(this.achievementToastTimer);
            }
        }
        this.achievementToastTimer = setTimeout(() => {
            this._hideAchievementToast(this._getUIScene());
        }, displayDuration);
    }

    _layoutToastContent(uiScene, background, title, name, icon, padding, spacing, iconSize, borderRadius, bgColor, borderColor) {
        if (!this.achievementToastContainer?.active || !background || !title || !name || !icon) return;

        const iconActualWidth = iconSize;
        const iconX = padding;
        const textX = iconX + iconActualWidth + spacing;

        const titleHeight = title.height;
        const nameHeight = name.height;
        const textBlockHeight = titleHeight + spacing + nameHeight;
        const contentHeight = Math.max(iconActualWidth, textBlockHeight);
        const totalHeight = contentHeight + padding * 2;

        const iconY = padding + contentHeight / 2;
        icon.setPosition(iconX + iconSize / 2, iconY);
        icon.setOrigin(0.5, 0.5);
        if (icon.type === 'Text') {
            icon.y += iconSize * 0.05;
        }

        const titleY = padding + (contentHeight - textBlockHeight) / 2;
        const nameY = titleY + title.height + spacing;
        title.setPosition(textX, titleY);
        name.setPosition(textX, nameY);

        const textWidth = Math.max(title.width, name.width);
        const totalWidth = textX + textWidth + padding;
        const minWidth = 250;
        const finalWidth = Phaser.Math.Clamp(totalWidth, minWidth, uiScene.cameras.main.width * 0.45);

        background.clear();
        background.fillStyle(bgColor, 0.95);
        background.fillRoundedRect(0, 0, finalWidth, totalHeight, borderRadius);
        background.lineStyle(2, borderColor, 1);
        background.strokeRoundedRect(0, 0, finalWidth, totalHeight, borderRadius);
        this.achievementToastContainer.setSize(finalWidth, totalHeight);
    }

    _hideAchievementToast(uiScene = this._getUIScene()) {
        if (!this.isShowingAchievementToast || !this.achievementToastContainer || !this.achievementToastContainer.scene || !uiScene) {
            this.isShowingAchievementToast = false;
            return;
        }
        uiScene.tweens.add({
            targets: this.achievementToastContainer,
            x: uiScene.cameras.main.width + 50,
            alpha: 0,
            duration: 300,
            ease: 'Cubic.easeIn',
            onComplete: () => {
                this.isShowingAchievementToast = false;
                if (this.achievementToastContainer?.scene) {
                    this.achievementToastContainer.setVisible(false);
                    this.achievementToastContainer.setPosition(0, 0);
                }
                this._processAchievementQueue();
            }
        });
    }

    clearAchievementQueue() {
        if (this.achievementToastQueue?.length > 0) {
            this.achievementToastQueue = [];
        }
        const uiScene = this._getUIScene();
        if (this.isShowingAchievementToast && this.achievementToastContainer && uiScene && !this.isLandingContext) {
            if (this.achievementToastTimer) {
                if (typeof this.achievementToastTimer.remove === 'function') {
                    this.achievementToastTimer.remove();
                } else {
                    clearTimeout(this.achievementToastTimer);
                }
            }
            this.achievementToastTimer = null;
            this._hideAchievementToast(uiScene);
        }
        if (this.isShowingAchievementToast && this.isLandingContext) {
            if (this.landingToastHideTimer) clearTimeout(this.landingToastHideTimer);
            this._hideLandingToast();
        }
    }

    _showLandingToast(achievementData) {
        if (this.isShowingAchievementToast) return;
        this.isShowingAchievementToast = true;

        let soundToPlay = null;
        if (achievementData.tier === 'platinum') soundToPlay = 'platinum';
        else if (['bronze', 'silver', 'gold'].includes(achievementData.tier)) soundToPlay = 'bronze_silver_gold';

        if (soundToPlay && window.audioManager?.play_sound) {
            window.audioManager.play_sound(soundToPlay);
        }

        const toastElement = document.getElementById('landing-achievement-toast');
        const iconImgElement = document.getElementById('landing-toast-icon-img');
        const iconEmojiElement = document.getElementById('landing-toast-icon-emoji');
        const nameElement = document.getElementById('landing-toast-name');

        if (!toastElement || !iconImgElement || !iconEmojiElement || !nameElement) {
            this.isShowingAchievementToast = false;
            return;
        }

        nameElement.textContent = achievementData.name || '???';
        const parcelIconUrl = achievementData.icon;

        if (parcelIconUrl) {
            iconImgElement.src = parcelIconUrl;
            iconImgElement.style.display = 'block';
            iconEmojiElement.style.display = 'none';
        } else {
            iconImgElement.style.display = 'none';
            iconEmojiElement.textContent = getTierEmoji(achievementData.tier);
            iconEmojiElement.style.display = 'block';
        }

        toastElement.className = 'landing-toast';
        toastElement.classList.add(`tier-${achievementData.tier || 'default'}`);

        if (this.landingToastHideTimer) clearTimeout(this.landingToastHideTimer);
        this.landingToastHideTimer = null;

        toastElement.classList.remove('achievement-toast-hidden', 'achievement-toast-hiding');
        toastElement.classList.add('achievement-toast-visible');

        const displayDuration = 3000;
        this.landingToastHideTimer = setTimeout(() => {
            this._hideLandingToast();
        }, displayDuration);
    }

    _hideLandingToast() {
        const toastElement = document.getElementById('landing-achievement-toast');
        if (!toastElement || !this.isShowingAchievementToast) {
            this.isShowingAchievementToast = false;
            return;
        }
        if (this.landingToastHideTimer) clearTimeout(this.landingToastHideTimer);
        this.landingToastHideTimer = null;

        toastElement.classList.add('achievement-toast-hiding');
        toastElement.classList.remove('achievement-toast-visible');

        const hideAnimationDuration = 300;
        setTimeout(() => {
            if (toastElement.classList.contains('achievement-toast-hiding')) {
                toastElement.classList.add('achievement-toast-hidden');
                toastElement.classList.remove('achievement-toast-hiding');
            }
            this.isShowingAchievementToast = false;
            this._processAchievementQueue();
        }, hideAnimationDuration);
    }

    shutdown() {
        this.isInitialized = false;
        this.unlockedInSession.clear();
        document.removeEventListener('firebase-auth', this.boundHandleAuthEvent);

        if (!this.isLandingContext && this.gameEventEmitter) {
            const gameEventsToUnlisten = [
                'scoreUpdated', 'gameWon', 'gameOver', 'pelletCollected', 'powerPelletCollected',
                'ghostEatenInPowerMode', 'massaraCollected', 'furlaniFuryActivated',
                'cardinaleFuryActivated', 'florenziTouched', 'kessieExpired', 'kessieRenewed',
                'origiSlowdownActivated', 'florenziRiscattoCompleted', 'levelCompleted',
                'florenziFastRiscattoCompleted', 'cardinaleFurySurvivedWithoutPDF',
                'origiSlowdownSurvived', 'progressTrackerUpdated', 'levelStatUpdated',
                'transitionStarted', 'transitionEnded'
            ];
            gameEventsToUnlisten.forEach(eventName => {
                try {
                    this.gameEventEmitter.off(eventName, undefined, this);
                } catch (e) {}
            });
            this.gameEventEmitter = null;
        }

        if (this.achievementToastTimer) {
            if (typeof this.achievementToastTimer.remove === 'function') {
                this.achievementToastTimer.remove();
            } else {
                clearTimeout(this.achievementToastTimer);
            }
        }
        this.achievementToastTimer = null;
        if (this.landingToastHideTimer) {
            clearTimeout(this.landingToastHideTimer);
        }
        this.landingToastHideTimer = null;

        if (this.achievementToastContainer && typeof this.achievementToastContainer.destroy === 'function') {
            try {
                const uiScene = this._getUIScene();
                if (uiScene) uiScene.tweens.killTweensOf(this.achievementToastContainer);
                this.achievementToastContainer.destroy();
            } catch (e) {}
        }
        this.achievementToastContainer = null;

        this.achievementToastQueue = [];
        this.pendingLandingNotifications = [];
        this.isShowingAchievementToast = false;
        this.uiSceneRef = null;
        this.isTransitioning = false;
    }
}

window.AchievementManager = AchievementManager;
// console.log("[AchMgr Modular v1.32.1] AchievementManager class defined.");

// --- END FILE: public/js/managers/AchievementManager.js ---